import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormCheckbox from 'components/KNForm/KNFormCheckbox'
import KNFormDropdown from 'components/KNForm/KNFormDropdown'
import KNFormText from 'components/KNForm/KNFormText'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processServerErrorMessages } from 'global/helpers/form'
import type { WithId } from 'global/helpers/requests'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CARRIERS_QUERY_KEY, editCarrier } from './CarrierManager.service'
import type { CarrierData, EditCarrierFormValues } from './CarrierManager.types'

interface EditCarrierDialogPayload {
  readonly carrier: CarrierData
}

interface EditCarrierDialogProps {
  readonly payload: EditCarrierDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

const EditCarrierDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: EditCarrierDialogProps): ReactElement => {
  const { t } = useTranslation()
  const {
    handleSubmit,
    watch,
    reset,
    control,
    formState,
    setValue,
    setError,
  } = useForm<EditCarrierFormValues>()
  const watchedTelematics = watch('telematics')
  const queryClient = useQueryClient()
  const updateCarrierMutation = useMutation({
    mutationFn: (data: WithId<EditCarrierFormValues>) => editCarrier(data.id, data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CARRIERS_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset({
        customerIdentifier: payload?.carrier.customerIdentifier,
        name: payload?.carrier.name,
        integration: payload?.carrier.integration,
        telematics: payload?.carrier.telematics,
        telematicsCredentials: payload?.carrier.telematicsCredentials,
        recommendedRoutes: payload?.carrier.recommendedRoutes,
        automaticWaypointUpdatesAllowed: payload?.carrier.automaticWaypointUpdatesAllowed,
      })
    }
  }, [open])

  useEffect(() => {
    if (watchedTelematics === undefined || watchedTelematics === 'NONE') {
      setValue('telematicsCredentials', undefined)
    }
  }, [watchedTelematics])

  const onSubmit: SubmitHandler<EditCarrierFormValues> = async (data: EditCarrierFormValues) => {
    await handleFormSubmission(() => updateCarrierMutation.mutateAsync({ id: payload.carrier.cid, ...data }), {
      onError: (error) => setError('root', processServerErrorMessages(error)),
      onSuccess: () => {
        onAction()
        analyticsEvent('polestar_cs_carrier_edited')
      },
    })
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={
        payload
          ? `${payload.carrier.name} — ${t('screens.cs.carrier_manager.card.actions.edit')}`
          : t('screens.cs.carrier_manager.card.actions.new')
      }
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
        >
          {payload
            ? t('screens.cs.carrier_manager.add_edit_carrier.update')
            : t('screens.cs.carrier_manager.add_edit_carrier.create')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Grid container spacing={2} mb={2}>
        <Grid size={{ xs: 12 }}>
          <KNFormText
            name="name"
            label={t('screens.cs.carrier_manager.add_edit_carrier.name')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
            readonly
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <KNFormText
            name="customerIdentifier"
            label={t('screens.cs.carrier_manager.add_edit_carrier.customer_identifier')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
            readonly
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <KNFormDropdown
            name="integration"
            label={t('screens.cs.carrier_manager.add_edit_carrier.integration')}
            control={control}
            options={['NONE', 'I_BROKER', 'I_BROKER_V2'].map((integration) => ({
              value: integration,
              label: t(`screens.cs.carrier_manager.integrations.${integration}`),
            }))}
            rules={{
              required: t('form.validation.required'),
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} mb={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <KNFormDropdown
            name="telematics"
            label={t('screens.cs.carrier_manager.add_edit_carrier.telematics')}
            control={control}
            options={['NONE', 'S3PWEB', 'NICPLACE', 'MACRO_POINT'].map((telematics) => ({
              value: telematics,
              label: t(`screens.cs.carrier_manager.telematics.${telematics}`),
            }))}
            rules={{
              required: t('form.validation.required'),
            }}
          />
        </Grid>
        {watchedTelematics !== undefined && watchedTelematics !== 'NONE' && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <KNFormText
              name="telematicsCredentials"
              label={t('screens.cs.carrier_manager.add_edit_carrier.telematics_credentials')}
              control={control}
              rules={{
                required: t('form.validation.required'),
              }}
            />
          </Grid>
        )}
      </Grid>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <InputLabel>{t('screens.cs.carrier_manager.add_edit_carrier.features')}</InputLabel>
          <KNFormCheckbox
            name="recommendedRoutes"
            label={t('screens.cs.carrier_manager.add_edit_carrier.shortest_routes')}
            control={control}
          />
          <KNFormCheckbox
            name="automaticWaypointUpdatesAllowed"
            label={t('screens.cs.carrier_manager.add_edit_carrier.status_automation')}
            control={control}
          />
        </Grid>
      </Grid>
    </KNDialog>
  )
}

export default EditCarrierDialog
